// ────────────────────────────────────────────────────────────
// TITLE CHAIN — vertical chain with running-acreage rail
// ────────────────────────────────────────────────────────────
// Renders a TRACT's chain — the single source of truth. The deal
// page passes highlightKey/subjectLabel to view the same chain
// through the deal lens (subject party highlighted, subject NMA on
// the terminus); the tract page renders it neutral with an estate
// terminus. Chain data is never copied per deal.

import { useMemo, useState } from 'react';
import type { ChainNode, TractBundle, SubjectTrackStep } from '../types';
import { matchesPartyText } from '../data/derive';
import { authHeaders } from '../../lib/authHeaders';

const STATUS_LABEL: Record<string, string> = {
  verified: 'Confirmed',
  flagged: 'Needs review',
  pending: 'Tentative',
  current: 'Current',
};

export interface ChainViewProps {
  tract: TractBundle;
  /** Deal lens: partyMatchKey of the subject to highlight. */
  highlightKey?: string;
  subjectLabel?: string;
  subjectNma?: number | null;
  /** Deal lens: the subject's direct lineage; off-spine links dim. */
  spineLinkIds?: string[];
  /** Deal lens: parties the interest passed through (fan highlighting). */
  carrierKeys?: string[];
  /** Deal lens: PRs who signed spine conveyances without holding the interest. */
  executorKeys?: string[];
  /** Deal lens: subject's running interest through each estate fracture (gold track). */
  subjectTrack?: SubjectTrackStep[];
}

export function ChainView({
  tract, highlightKey, subjectLabel, subjectNma, spineLinkIds, carrierKeys, executorKeys, subjectTrack,
}: ChainViewProps) {
  const { chain, terminatesAtPatent, openEndNote } = tract;
  const spine = useMemo(
    () => (spineLinkIds ? new Set(spineLinkIds) : null),
    [spineLinkIds],
  );
  const trackByDoc = useMemo(() => {
    const m = new Map<string, SubjectTrackStep>();
    for (const s of subjectTrack ?? []) if (s.docNumber) m.set(s.docNumber, s);
    return m;
  }, [subjectTrack]);

  return (
    <div className="dd-card">
      <div className="dd-card-head">
        <div className="dd-card-title">Chain of <em>conveyance</em></div>
        <div className="dd-card-meta">
          {tract.trs} · {chain.length} links · oldest first
          {highlightKey && ' · deal lens'}
        </div>
      </div>
      <div className="dd-chain-legend">
        <span className="dd-legend-item"><span className="dd-legend-dot" style={{ background: 'var(--green)' }} /> confirmed</span>
        <span className="dd-legend-item"><span className="dd-legend-dot" style={{ background: 'var(--amber)' }} /> needs review</span>
        <span className="dd-legend-item"><span className="dd-legend-dot" style={{ background: 'var(--blue)' }} /> tentative / inferred</span>
        <span className="dd-legend-item"><span className="dd-legend-dot" style={{ background: 'var(--red)' }} /> not applied</span>
        <span className="dd-legend-item" style={{ color: 'var(--amber)' }}>▮ severance drops the rail</span>
      </div>

      <div className="dd-chain-body">
        {!terminatesAtPatent && (
          <div className="dd-openend">
            <div className="dd-openend-tag">◌ OPEN END · INCOMPLETE</div>
            <div className="dd-openend-sub">
              The chain does not reach a patent or sovereign conveyance. The 100% mineral
              root below is <strong>assumed, not proven</strong> · an unfound upstream
              reservation could reduce every figure on this page.
            </div>
            <div className="dd-openend-note">{openEndNote}</div>
          </div>
        )}

        {chain.map((link, i) => (
          <div key={link.id}>
            {link.gapYearsBefore && (
              <div className="dd-gap-row">
                <span className="dd-gap-label">— {link.gapYearsBefore} quiet years —</span>
              </div>
            )}
            <LinkRow
              link={link}
              first={i === 0}
              highlightKey={highlightKey}
              subjectNma={subjectNma}
              onSpine={spine ? spine.has(link.id) : null}
              carrierKeys={carrierKeys}
              executorKeys={executorKeys}
              subjectStep={link.docNumber ? trackByDoc.get(link.docNumber) : undefined}
            />
          </div>
        ))}

        <div className="dd-linkrow last">
          <div className="dd-rail" />
          {subjectLabel ? (
            <div className="dd-terminus">
              <div>
                <div className="dd-terminus-tag">SUBJECT</div>
                <div className="dd-terminus-name">{subjectLabel}</div>
              </div>
              <div className="dd-terminus-nma">
                <div className="n">{subjectNma ?? '?'} NMA</div>
                <div className="sub">
                  {tract.subjectShareLabel && <>{tract.subjectShareLabel} of the {fmtAcres(tract.estateAcres)} ac private estate · </>}
                  derived from chain · upper bound
                </div>
              </div>
            </div>
          ) : (
            <div className="dd-terminus neutral">
              <div>
                <div className="dd-terminus-tag">FRACTURED ESTATE</div>
                <div className="dd-terminus-name">{fmtAcres(tract.estateAcres)} ac private estate</div>
              </div>
              <div className="dd-terminus-nma">
                <div className="sub">
                  divided across {tract.parties.length} traced parties · see disposition
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── one chain row ──────────────────────────────────────────

function LinkRow({ link, first, highlightKey, subjectNma, onSpine, carrierKeys, executorKeys, subjectStep }: {
  link: ChainNode; first: boolean; highlightKey?: string; subjectNma?: number | null;
  /** true/false when a spine is computed (deal lens); null on the neutral tract view. */
  onSpine: boolean | null;
  carrierKeys?: string[];
  executorKeys?: string[];
  subjectStep?: SubjectTrackStep;
}) {
  // Severance links start expanded — they are the money rows.
  const [open, setOpen] = useState(link.severance);

  const subjectTouches = !!highlightKey && (
    link.grantorMatchKeys.includes(highlightKey) ||
    link.granteeMatchKeys.includes(highlightKey)
  );

  // Structural placement: spine carries the weight, branches recede,
  // not-applied events pull out of the flow entirely.
  const placement =
    link.notApplied ? 'offset' :
    onSpine === true ? 'spine' :
    onSpine === false ? 'branch' :
    '';

  // Chip tiers, strongest first: interest passed through them (spine) >
  // signed as PR without holding (executor). Interest carriage wins when
  // a name qualifies for both.
  const chipTier = (g: { matchKey: string; full: string }): 'spine' | 'executor' | '' => {
    if (carrierKeys &&
      (carrierKeys.includes(g.matchKey) || carrierKeys.some(c => matchesPartyText(g.full, c)))) {
      return 'spine';
    }
    if (executorKeys &&
      (executorKeys.includes(g.matchKey) || executorKeys.some(c => matchesPartyText(g.full, c)))) {
      return 'executor';
    }
    return '';
  };

  return (
    <div
      className={`dd-linkrow ${first ? 'first' : ''} ${placement} ${link.railDrop ? 'sev-row' : ''}`}
      id={link.docNumber ? `dd-doc-${link.docNumber}` : undefined}
    >
      <div className="dd-rail">
        {link.railDrop ? (
          <div className="dd-rail-drop" title={`Severance at ${link.railDrop.docNumber}: × ${link.railDrop.factor}`}>
            <span className="from">{fmtAcres(link.railDrop.before)}</span>
            <span className="arrow">▼ × {link.railDrop.factor}</span>
            <span className="to">{fmtAcres(link.railDrop.after)}</span>
          </div>
        ) : link.railAcres !== null ? (
          <div className="dd-rail-acres">{fmtAcres(link.railAcres)}</div>
        ) : (
          <div className="dd-rail-frac" title="Interest fractured across heirs below this point">÷ heirs</div>
        )}
        {subjectStep && (
          <div
            className={`dd-subj-track ${subjectStep.clean ? '' : 'unclean'}`}
            title={`Subject's running interest after this estate fracture: ${fmtTrackVal(subjectStep.after)} ac${subjectStep.fractionLabel ? ` (${subjectStep.fractionLabel} of ${fmtTrackVal(subjectStep.before)} ac to the subject's line)` : ''}`}
          >
            <span className="lbl">to subject</span>
            {subjectStep.fractionLabel && <span className="frac">× {subjectStep.fractionLabel}</span>}
            <span className="val">{fmtTrackVal(subjectStep.after)}</span>
          </div>
        )}
      </div>

      <div
        className={[
          'dd-link',
          link.severance ? 'severance' : '',
          link.notApplied ? 'not-applied' : '',
          link.isDistribution ? 'distribution' : '',
          subjectTouches ? 'subject-touch' : '',
          placement === 'branch' ? 'branch-dim' : '',
          placement === 'offset' ? 'offset-card' : '',
        ].join(' ')}
        onClick={() => setOpen(o => !o)}
        title={open ? undefined : 'Click to expand verbatim language and source document'}
      >
        {placement === 'offset' && (
          <div className="dd-offset-tag">Observed · not connected to the chain</div>
        )}
        <div className="dd-link-head">
          {link.docNumber && <span className="dd-doc-num">{link.docNumber}</span>}
          <span className="dd-doc-type">{link.docType}</span>
          <span className="dd-doc-date">{fmtDate(link.date)}</span>
          <span className="spacer" />
          {subjectTouches && <span className="dd-badge subject">Subject</span>}
          {link.severance && <span className="dd-badge severance">Severance</span>}
          {link.competitorPurchase && <span className="dd-badge competitor">Competitor · {link.buyerName}</span>}
          {link.branchOwner && !link.competitorPurchase && !link.isDistribution && (
            <span className="dd-badge branch">{link.branchOwner} branch</span>
          )}
          {link.notApplied && <span className="dd-badge not-applied">Not applied · gap</span>}
          <span className={`dd-badge status-${link.status}`}>{STATUS_LABEL[link.status]}</span>
        </div>

        <div className="dd-conveyance">
          <strong className={highlightKey && link.grantorMatchKeys.includes(highlightKey) ? 'dd-hl' : ''}>{link.grantor}</strong>
          <span className="arrow">→</span>
          {link.isDistribution && link.fan
            ? <span className="grantee-count">{link.fan.length} grantees (estate distribution)</span>
            : <strong className={highlightKey && link.granteeMatchKeys.includes(highlightKey) ? 'dd-hl' : ''}>{link.grantees.join('; ')}</strong>}
        </div>

        {link.interestSummary && (
          <div className="dd-interest">{link.interestSummary}</div>
        )}

        {link.dupNote && (
          <div className="dd-dupnote">⚠ {link.dupNote}</div>
        )}

        {link.shadow && (
          <div className="dd-shadowbox">
            <span className="dd-shadow-tag">TENTATIVE · NOT APPLIED TO NUMBERS</span>
            If confirmed: all downstream figures reduce ×{link.shadow.factor}
            {subjectNma != null && (
              <> · subject {subjectNma} → <strong>{round4(subjectNma * link.shadow.multiplier)}</strong> NMA</>
            )}
          </div>
        )}

        {link.isDistribution && link.fan && (
          <div className="dd-fan">
            <div className="dd-fan-label">
              ⑃ Estate of {link.distributionOf} · fractures into {link.fan.length}
              {link.branchOwner ? ` · on the ${link.branchOwner} branch` : ''}
            </div>
            <div className="dd-fan-chips">
              {link.fan.map((g, i) => {
                const isSubject = !!highlightKey && g.matchKey === highlightKey;
                const tier = isSubject ? '' : chipTier(g);
                const title =
                  tier === 'spine' ? `${g.full} · held and passed the interest` :
                  tier === 'executor' ? `${g.full} · signed as personal representative · no interest passed through them personally` :
                  g.full;
                return (
                  <span
                    key={i}
                    className={`dd-fan-chip ${isSubject ? 'subject' : ''} ${tier}`}
                    title={title}
                  >
                    {g.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {open && (
          <div className="dd-link-expand" onClick={e => e.stopPropagation()}>
            {link.reservedQuote && (
              <blockquote className="dd-quote reserved">
                <span className="dd-quote-label">Reservation · verbatim</span>
                “{link.reservedQuote}”
              </blockquote>
            )}
            {link.grantingQuote && (
              <blockquote className="dd-quote">
                <span className="dd-quote-label">Granting language · verbatim</span>
                “{link.grantingQuote}”
              </blockquote>
            )}
            {!link.reservedQuote && !link.grantingQuote && (
              <div className="dd-interest">No extracted language stored for this link.</div>
            )}
            {link.fileId && <DocButton fileId={link.fileId} fileName={link.fileName} />}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── source document opener with pending state ──────────────

type DocState = 'idle' | 'checking' | 'pending';

function DocButton({ fileId, fileName }: { fileId: string; fileName?: string }) {
  const [state, setState] = useState<DocState>('idle');

  const onView = async () => {
    setState('checking');
    try {
      const r = await fetch(`/api/files/${fileId}`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!r.ok) { setState('pending'); return; }
      const blob = await r.blob();
      window.open(URL.createObjectURL(blob), '_blank');
      setState('idle');
    } catch {
      setState('pending');   // network error (e.g. vite dev without functions)
    }
  };

  return (
    <div className="dd-doc-row">
      {fileName && <span className="dd-doc-file">{fileName}</span>}
      {state === 'pending' ? (
        <span className="dd-doc-pending">Document image pending · R2 mirroring has not landed yet</span>
      ) : (
        <button className="dd-doc-btn" onClick={onView} disabled={state === 'checking'}>
          {state === 'checking' ? 'Opening…' : 'View document ↗'}
        </button>
      )}
    </div>
  );
}

// ─── formatting ─────────────────────────────────────────────

function fmtAcres(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
}

/** Subject-track values keep 4-decimal precision (4.4444, 0.8889), trimmed. */
function fmtTrackVal(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(4).replace(/\.?0+$/, '');
}

function fmtDate(iso: string): string {
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}
